import { FormControl } from '@angular/forms';
import moment from 'moment';

import {
  DATE_FORMAT_FRIENDLY,
  DATE_FORMAT_ODATA,
  DATE_FORMAT_SAP,
  DATE_FORMAT_SHAREPOINT,
  DATE_FORMAT_SHORT_WEEKDAY_NAME,
} from '@shared-constants/date-format-constants';
import { IDayOfWeek } from '@shared-interfaces/dates';

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'ddd (MM/DD)',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export class DateHandler {
  //If / when we go to replace this with standard date or a new library, unit tests could help.

  selectedDate: FormControl<moment.Moment>;
  previousDate: moment.Moment;

  constructor(date?: string | moment.Moment) {
    if (!date) this.selectedDate = new FormControl(moment().startOf('day'), { nonNullable: true });
    this.selectedDate = new FormControl(moment(date).startOf('day'), { nonNullable: true });
    this.syncPreviousDate();
  }

  isCurrentWeek(momentObj: moment.Moment = this.selectedDate.value): boolean {
    return this.isSameWeek(this.getToday(), momentObj);
  }

  toWeDate() {
    this.setDate(this.getWeDate());
    return this;
  }

  getToday(): moment.Moment {
    return moment();
  }

  getUnixTime(momentObj: moment.Moment = this.selectedDate.value) {
    return momentObj.valueOf();
  }

  getUnixString(momentObj?: moment.Moment) {
    const seconds = this.getUnixTime(momentObj).toString();
    return `/Date(${seconds})/`;
  }

  //We use an overload methods here so that TS can send correct typing back depending on what parameters are given
  getSapDateString(isMoment?: false): string;
  getSapDateString(isMoment: true): moment.Moment;
  getSapDateString<T extends boolean | undefined>(isMoment?: T): string | moment.Moment {
    const momentObj = this.selectedDate.value;
    const clone = momentObj.clone();
    return isMoment ? clone : clone.format(DATE_FORMAT_SAP);
  }

  getSapWeDateString(momentObj: moment.Moment = this.selectedDate.value): string {
    return momentObj.day(6).format(DATE_FORMAT_SAP);
  }

  getHumanReadableDateString(momentObj: moment.Moment = this.selectedDate.value): string {
    return momentObj.format(DATE_FORMAT_FRIENDLY);
  }

  getSharepointFormat(momentObj: moment.Moment = this.selectedDate.value): string {
    return momentObj.format(DATE_FORMAT_SHAREPOINT);
  }

  getWeDate(momentObj?: moment.Moment): moment.Moment {
    if (momentObj) return momentObj.day(6);
    else return moment(this.selectedDate.value.clone().day(6));
  }

  getWeekStartingDate(momentObj?: moment.Moment): moment.Moment {
    if (moment.isMoment(momentObj)) return momentObj.day(0);
    else return moment(this.selectedDate.value.clone().day(0));
  }

  private getOdataString(momentObj?: moment.Moment): string {
    if (momentObj) return momentObj.format(DATE_FORMAT_ODATA);
    else return this.selectedDate.value.clone().format(DATE_FORMAT_ODATA);
  }

  getFilterString(momentObj?: moment.Moment): string {
    if (momentObj) return `datetime'${this.getOdataString(momentObj)}'`;
    else return `datetime'${this.getOdataString()}'`;
  }

  getControl(): FormControl<moment.Moment> {
    return this.selectedDate;
  }

  getMoment(): moment.Moment {
    return this.selectedDate.value.clone();
  }

  private syncPreviousDate(): void {
    this.previousDate = this.selectedDate.value.clone();
  }

  private isSameWeek(item1: moment.Moment, item2: moment.Moment): boolean {
    return moment(item1).isSame(item2, 'week');
  }

  getDayString(momentObj: moment.Moment = this.selectedDate.value) {
    return momentObj.format(DATE_FORMAT_SHORT_WEEKDAY_NAME).toLowerCase();
  }

  convertDayOfWeekToMoment(weekDay: IDayOfWeek) {
    return this.getWeekStartingDate().day(weekDay);
  }

  subtractMonths(months: number): moment.Moment {
    return this.selectedDate.value.clone().subtract(months, 'months');
  }

  setDate(value: moment.Moment) {
    this.selectedDate.setValue(value);
  }

  setWeDateWithString(string: string, shouldTrigger: boolean) {
    const date = moment(string).day(6);
    this.selectedDate.setValue(date, {
      emitEvent: shouldTrigger,
    });
  }

  decrementWeDate() {
    this.selectedDate.setValue(this.selectedDate.value.subtract(1, 'week'));
  }

  incrementWeDate() {
    this.selectedDate.setValue(this.selectedDate.value.add(1, 'week'));
  }
}
